import { inject, Injectable } from '@angular/core';
import { Client, ClientDTO } from '../models/client.model';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogService } from '../../shared/services/dialog.service';
import { CLIENT_FORM } from '../models/form/client-form';
import { ApiService } from '../../shared/services';
import { HttpParams } from '@angular/common/http';
import { PayloadRequest, PayloadResponse } from '../models/payload.model';
import { AuthService } from '../../auth/services/auth.service';
import { FilterFieldMap } from '../models';
import { trimFormValues } from '../../shared/utils/form.utils';

@Injectable({
	providedIn: 'root'
})
export class ClientService {
	private apiService = inject(ApiService);
	private authService = inject(AuthService);
	private dialogService = inject(DialogService);
	private referentFields = [
		'referentName',
		'referentSurname',
		'referentEmail',
		'referentFiscalCode',
		'referentBirthDate',
		'referentBirthPlace',
		'referentPostalCode',
		'referentRole'
	];

	constructor(
		private fb: FormBuilder,
		private router: Router
	) {}

	createClientFormGroup() {
		const formBuilder = new FormBuilder();
		const formControls: { [key: string]: FormControl } = {};

		Object.keys(CLIENT_FORM).forEach(key => {
			const field = CLIENT_FORM[key];
			formControls[key] = new FormControl(field.value, field.validators);
		});
		return formBuilder.group(formControls);
	}

	loadClientData(id: number, form: FormGroup): Observable<boolean> {
		return this.getClientById(id).pipe(
			map(client => {
				form.patchValue(client);
				return client?.referentEmail;
			})
		);
	}

	navigateToList(): void {
		this.router.navigate(['/main/clienti']);
	}

	getClientFormFields(): Observable<any> {
		return of(CLIENT_FORM);
	}

	getClientsList(
		filter: FilterFieldMap,
		pageNumber?: number,
		pageSize?: number
	): Observable<PayloadResponse<Client[]>> {
		let params = new HttpParams();
		if (filter) {
			if (filter['reseller'])
				params = params.set('resellerId', filter['reseller'].value);
			if (filter['businessName'].value)
				params = params.set('businessName', filter['businessName'].value);
			if (filter['email'].value)
				params = params.set('email', filter['email'].value);
			if (filter['isActive'].value)
				params = params.set('isActive', filter['isActive'].value);
		}

		if (pageNumber) params = params.set('pageNumber', pageNumber);
		if (pageSize) params = params.set('pageSize', pageSize);

		return this.apiService.get('clients', { params: params });
	}

	getClientsListByResellerId(
		resellerId: number
	): Observable<PayloadResponse<Client[]>> {
		let params = new HttpParams();
		params = params.set('resellerId', resellerId);
		return this.apiService.get('clients', { params: params });
	}

	countItems(
		resellerId?: number
	): Observable<{ activeItems: number; inactiveItems: number }> {
		let params = new HttpParams();
		if (resellerId) params = params.set('resellerId', resellerId);
		return this.apiService
			.get(`clients/count-status`, { params: params })
			.pipe(map(payload => payload.data));
	}

	getResellerList(): Observable<{ id: number; name: string }[]> {
		return this.apiService
			.get('clients/reseller-list')
			.pipe(map(payload => payload.data));
	}

	getLastInsertedClients(): Observable<Client[]> {
		return this.apiService.get('clients/last-inserted').pipe(
			map(res => {
				return res.data;
			})
		);
	}

	getClientById(clientId: number): Observable<any> {
		return this.apiService.get(`clients/${clientId}`).pipe(
			map(client => {
				return {
					...client.data,
					nationId: client.data.nation.id ?? null,
					resellerId: client.data.reseller.resellerId ?? null,
					atecoCodeId: client.data.atecoCode.id ?? null,
					cityId: client.data.city?.id ?? null,
					referentBirthPlaceId: client.data.referentBirthPlace?.id ?? null
				};
			})
		);
	}

	onSubmitForm(
		clientId: number,
		formClient: FormGroup
	): Observable<PayloadResponse<Client>> {
		trimFormValues(formClient);

		if (formClient.invalid) {
			formClient.markAllAsTouched();

			this.dialogService.openDialogInfoNoRedirect(
				'Errore',
				'Compila tutti i campi obbligatori.'
			);

			return of();
		}

		const isUpdate = !!clientId;
		const client = this.convertTypeForm(formClient);
		const observable = isUpdate
			? this.updateClient(client, clientId)
			: this.saveClient(client);

		return observable.pipe(tap(() => this.showDialogSuccess(isUpdate)));
	}

	private saveClient(client: ClientDTO): Observable<PayloadResponse<Client>> {
		return this.authService.getResellerByUserIdLogged().pipe(
			switchMap(reseller => {
				client.resellerId = reseller.data.resellerId;
				const payload = new PayloadRequest<ClientDTO>({ data: client });
				return this.apiService.post('clients/create', payload);
			})
		);
	}

	private updateClient(
		client: ClientDTO,
		clientId: number
	): Observable<PayloadResponse<Client>> {
		const payload = new PayloadRequest<ClientDTO>({ data: client });
		return this.apiService.patch(`clients/update/${clientId}`, payload);
	}

	private showDialogSuccess(isUpdate: boolean): void {
		const dialogMessage = isUpdate
			? 'Il cliente è stato modificato con successo'
			: 'Il cliente è stato creato con successo';

		this.dialogService.openDialogAlert(
			'Operazione completata',
			dialogMessage,
			() => {
				this.router.navigate(['/main/clienti']);
			}
		);
	}

	toggleStatusClient(client: Client): Observable<PayloadResponse<Client>> {
		const clientId = client.clientId;
		return this.apiService.patch(`clients/${clientId}/toggle-status`, null);
	}

	toggleShowReferentClient(
		showReferente: boolean,
		formClient: FormGroup
	): boolean {
		if (!showReferente) {
			this.setValidationClient(formClient);
			formClient.markAllAsTouched();
		} else {
			this.resetValidationClient(formClient);
		}
		showReferente = !showReferente;
		return showReferente;
	}

	setValidationClient(formClient: FormGroup): void {
		this.referentFields.forEach(fieldName => {
			const control = formClient.get(fieldName);

			control?.setValidators(CLIENT_FORM[fieldName].validators);
			control?.updateValueAndValidity();
		});
	}

	resetValidationClient(formClient: FormGroup): void {
		this.referentFields.forEach(fieldName => {
			const control = formClient.get(fieldName);

			control?.reset();
			control?.clearValidators();

			control?.updateValueAndValidity();
		});
	}

	convertTypeForm(form: FormGroup): ClientDTO {
		const client: ClientDTO = {
			nationId: Number(form.value.nationId),
			resellerId: Number(form.value.resellerId),
			isActive: form.value.isActive === true || form.value.isActive === 'true',
			businessName: form.value.businessName,
			email: form.value.email,
			uniqueCode: form.value.uniqueCode,
			atecoCodeId: Number(form.value.atecoCodeId),
			address: form.value.address,
			cityId: Number(form.value.cityId),
			postalCode: form.value.postalCode,
			pIvaTaxId: form.value.pIvaTaxId,
			isAutomaticRelease:
				form.value.isAutomaticRelease === true ||
				form.value.isAutomaticRelease === 'true',
			referentName: form.value.referentName ?? null,
			referentSurname: form.value.referentSurname ?? null,
			referentEmail: form.value.referentEmail ?? null,
			referentFiscalCode: form.value.referentFiscalCode ?? null,
			referentBirthDate: form.value.referentBirthDate ?? null,
			referentBirthPlaceId:
				form.value.referentBirthPlaceId > 0
					? Number(form.value.referentBirthPlaceId)
					: null,
			referentPostalCode: form.value.referentPostalCode ?? null,
			referentRole: form.value.referentRole ?? null
		};
		return client;
	}

	// convertClientToClientDTO(client: Client): ClientDTO {
	// 	let clientDTO: ClientDTO = {
	// 		clientId: client.clientId,
	// 		nationId: client.nation.id,
	// 		resellerId: client.reseller.resellerId,
	// 		isActive: client.isActive,
	// 		businessName: client.businessName,
	// 		email: client.email,
	// 		uniqueCode: client.uniqueCode,
	// 		atecoCode: client.atecoCode.id,
	// 		address: client.address,
	// 		city: client.city,
	// 		postalCode: client.postalCode,
	// 		province: client.province,
	// 		pIvaTaxId: client.pIvaTaxId,
	// 		isAutomaticRelease: client.isAutomaticRelease,
	// 		referentName: client.referentName,
	// 		referentSurname: client.referentSurname,
	// 		referentEmail: client.referentEmail,
	// 		referentFiscalCode: client.referentFiscalCode,
	// 		referentBirthDate: client.referentBirthDate,
	// 		referentBirthPlace: client.referentBirthPlace,
	// 		referentPostalCode: client.referentPostalCode,
	// 		referentProvince: client.referentProvince,
	// 		referentRole: client.referentRole
	// 	};
	// 	return clientDTO;
	// }
}
